import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { Alert, Box, Group, Progress, Radio, Stack } from '@mantine/core'
import { AppBadge, AppButton, AppText } from '../../app/common/ui'
import { toast } from 'react-toastify'
import { IoCheckmarkCircle } from 'react-icons/io5'
import agent from '../../app/api/agent'
import type { PollDto } from '../../app/models/poll'
import { useStore } from '../../app/stores/store'

function formatTimeLeft (expiresAt: string): string {
  const ms = new Date(expiresAt).getTime() - Date.now()
  if (ms <= 0) return 'Poll ended'
  const h = Math.floor(ms / 3600000)
  const m = Math.floor((ms % 3600000) / 60000)
  return h > 0 ? `${h}h ${m}m remaining` : `${m}m remaining`
}

export default observer(function PollDetailPage () {
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const { userStore } = useStore()
  const [poll, setPoll] = useState<PollDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null)
  const [voting, setVoting] = useState(false)

  const load = useCallback(() => {
    if (!id) return
    setLoading(true)
    agent.Polls.get(id)
      .then((p) => {
        setPoll(p)
        setSelectedOptionId(p.userVoteOptionId)
      })
      .catch(() => setPoll(null))
      .finally(() => setLoading(false))
  }, [id])

  useEffect(() => {
    load()
  }, [load])

  const handleVote = async () => {
    if (!poll || !selectedOptionId || !userStore.isLoggedIn) {
      if (!userStore.isLoggedIn) history.push('/login')
      return
    }
    if (poll.isExpired) {
      toast.info('This poll has ended.')
      return
    }
    setVoting(true)
    try {
      const updated = await agent.Polls.vote(poll.id, selectedOptionId)
      setPoll(updated)
      setSelectedOptionId(updated.userVoteOptionId)
      toast.success('Vote saved')
    } catch {
      /* toast from interceptor */
    } finally {
      setVoting(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ padding: 24, maxWidth: 640, margin: '0 auto' }}>
        <AppText color="dimmed">Loading…</AppText>
      </Box>
    )
  }

  if (!poll) {
    return (
      <Box sx={{ padding: 24, maxWidth: 640, margin: '0 auto' }}>
        <AppText>Poll not found.</AppText>
        <AppButton component={Link} to="/polls" variant="light" mt="md">
          Back to polls
        </AppButton>
      </Box>
    )
  }

  const showCounts = poll.canSeeResults
  const votedOption = poll.userVoteOptionId
    ? poll.options.find((o) => o.id === poll.userVoteOptionId)
    : null

  return (
    <Box sx={{ padding: '16px 24px', maxWidth: 640, margin: '0 auto' }}>
      <AppButton component={Link} to="/polls" variant="subtle" size="xs" mb="md">
        ← Polls
      </AppButton>

      <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 22, mb: 8 }}>
        {poll.question}
      </AppText>

      {poll.description && (
        <AppText size="sm" color="dimmed" mb="sm">
          {poll.description}
        </AppText>
      )}

      <Group spacing="xs" mb="md">
        <AppText size="xs" color="dimmed">
          @{poll.authorUsername ?? 'unknown'}
        </AppText>
        <AppText size="xs" color="dimmed">· {formatTimeLeft(poll.expiresAt)}</AppText>
        <AppBadge size="xs" variant="light" color="gray">Sitewide</AppBadge>
        {poll.allowEarlyReveal && !poll.isExpired && (
          <AppBadge size="xs" variant="light" color="blue">Results visible early</AppBadge>
        )}
      </Group>

      {/* "You voted" callout */}
      {votedOption && (
        <Alert
          icon={<IoCheckmarkCircle size={16} />}
          color="green"
          mb="md"
          sx={{ padding: '8px 12px' }}
        >
          <AppText size="sm">You voted: <strong>{votedOption.text}</strong></AppText>
        </Alert>
      )}

      {showCounts && (
        <Stack spacing="md" mb="lg">
          {poll.options.map((o) => (
            <Box key={o.id}>
              <Group position="apart" mb={4}>
                <AppText size="sm">{o.text}</AppText>
                <AppText size="xs" color="dimmed">
                  {o.voteCount ?? 0}
                  {o.votePercent != null ? ` (${o.votePercent}%)` : ''}
                </AppText>
              </Group>
              <Progress
                value={o.votePercent ?? 0}
                size="sm"
                color={poll.userVoteOptionId === o.id ? 'orange' : 'gray'}
              />
            </Box>
          ))}
          <AppText size="xs" color="dimmed">
            {poll.totalVotes} total vote{poll.totalVotes === 1 ? '' : 's'}
          </AppText>
        </Stack>
      )}

      {!poll.isExpired && userStore.isLoggedIn && (
        <>
          <Radio.Group value={selectedOptionId ?? ''} onChange={setSelectedOptionId} mb="md">
            <Stack spacing="xs">
              {poll.options.map((o) => (
                <Radio key={o.id} value={o.id} label={o.text} />
              ))}
            </Stack>
          </Radio.Group>
          <AppButton
            color="ember"
            loading={voting}
            disabled={!selectedOptionId || selectedOptionId === poll.userVoteOptionId}
            onClick={() => void handleVote()}
          >
            {poll.userVoteOptionId ? 'Update vote' : 'Vote'}
          </AppButton>
        </>
      )}

      {!poll.isExpired && !userStore.isLoggedIn && (
        <AppButton component={Link} to="/login" color="ember">
          Log in to vote
        </AppButton>
      )}

      {!poll.canSeeResults && !poll.isExpired && (
        <AppText size="xs" color="dimmed" mt="sm">
          Results are hidden until this poll ends.
        </AppText>
      )}

      {poll.isExpired && !showCounts && (
        <AppText size="xs" color="dimmed" mt="sm">
          This poll has ended.
        </AppText>
      )}
    </Box>
  )
})
